/**
 * Request Interceptor for ArbiWin API Client
 *
 * This module handles all outgoing HTTP requests, including:
 * - JWT token management and injection
 * - Token refresh logic for expired tokens
 * - Request authentication and authorization
 * - Request logging and monitoring
 * - Security headers and CSRF protection
 *
 * Following constitution requirements for API integration
 * and security best practices.
 */

import { logError } from '../../../utils/errorHandler';
import { getEnvironmentConfig } from '../../../config/environment';
import { TokenManager } from './TokenManager';

/**
 * Token refresh manager
 */
const TokenRefreshManager = {
  refreshPromise: null,
  isRefreshing: false,

  /**
   * Perform token refresh
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<string|null>} New access token or null if failed
   */
  async performTokenRefresh(refreshToken) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Attempting token refresh...');
      }
      
      // Import AuthService dynamically to avoid circular dependency
      const { default: AuthService } = await import('../../auth/services/AuthService');
      const { data, error } = await AuthService.refreshToken(refreshToken);
      
      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Token refresh failed:', error.message);
        } else {
          console.error('Token refresh failed');
        }
        logError(error, 'TOKEN_REFRESH_ERROR', {
          message: 'Failed to refresh authentication token'
        });
        return null;
      }

      if (data && data.accessToken) {
        // Store new tokens
        TokenManager.setTokens(
          data.accessToken,
          data.refreshToken || refreshToken,
          data.expiresIn || 1800 // Default 30 minutes
        );

        if (process.env.NODE_ENV === 'development') {
          console.log('Token refreshed successfully');
        }
        return data.accessToken;
      }

      console.warn('No access token in refresh response');
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Token refresh failed:', error);
      } else {
        console.error('Token refresh failed');
      }

      logError(error, 'TOKEN_REFRESH_ERROR', {
        message: 'Failed to refresh authentication token'
      });

      // Clear invalid tokens
      TokenManager.clearTokens();

      return null;
    }
  },

  /**
   * Get refresh promise or create new one
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<string|null>} Refresh promise
   */
  getRefreshPromise(refreshToken) {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh(refreshToken);
    
    // Clear promise when done
    this.refreshPromise.finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }
};

/**
 * Check if endpoint is public (doesn't require custom headers)
 * @param {string} url - Request URL
 * @returns {boolean} True if public endpoint
 */
function isPublicEndpoint(url) {
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/change-password',
    '/auth/activate-subscription',
    '/auth/cancel-subscription',
    '/auth/subscriptions/status',
    '/health'
  ];
  
  return publicEndpoints.some(endpoint => url && url.includes(endpoint));
}

/**
 * Request interceptor factory
 * @param {Object} apiClient - Axios instance
 * @returns {Function} Request interceptor
 */
export const createRequestInterceptor = (apiClient) => {
  return function(config) {
    const requestId = generateRequestId();
    const startTime = Date.now();
    
    // Store request metadata
    config.metadata = {
      requestId,
      startTime,
      url: config.url,
      method: config.method?.toUpperCase() || 'GET'
    };

    // Add request ID header only for non-public endpoints and when not using minimal headers
    config.headers = config.headers || {};
    
    // Check if minimal headers mode is requested
    const useMinimalHeaders = isPublicEndpoint(config.url) || config.minimalHeaders;
    
    if (useMinimalHeaders) {
      // Minimal headers mode - only essential headers
      if (process.env.NODE_ENV === 'development') {
        console.log('Using minimal headers for endpoint:', config.url);
      }
      addMinimalHeaders(config);
      delete config.minimalHeaders;
    } else {
      // Full headers mode - add request tracking and security headers
      if (process.env.NODE_ENV === 'development') {
        console.log('Adding security headers for protected endpoint:', config.url);
      }
      config.headers['x-request-id'] = requestId;
      addSecurityHeaders(config);
    }

    // Add authentication if required (always, regardless of minimal headers)
    if (config.auth !== false) {
      addAuthentication(config);
    }

    // Add custom headers if specified
    if (config.customHeaders) {
      Object.assign(config.headers, config.customHeaders);
      delete config.customHeaders;
    }

    // Log request
    logRequest(config);

    return config;
  };
};

/**
 * Add security headers to request
 * @param {Object} config - Axios config
 */
function addSecurityHeaders(config) {
  const headers = config.headers || {};
  
  // CSRF protection
  headers['X-Requested-With'] = 'XMLHttpRequest';
  
  // Cache control
  headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  headers['Pragma'] = 'no-cache';
  headers['Expires'] = '0';
  
  // Content type
  if (!headers['Content-Type'] && (config.data || config.method === 'POST' || config.method === 'PUT' || config.method === 'PATCH')) {
    headers['Content-Type'] = 'application/json';
  }
  
  config.headers = headers;
}

/**
 * Add minimal headers for specific requests
 * @param {Object} config - Axios config
 */
function addMinimalHeaders(config) {
  const headers = config.headers || {};
  
  // Only essential headers
  headers['Accept'] = 'application/json';
  
  // Add secure API key if configured
  const envConfig = getEnvironmentConfig();
  if (envConfig?.secureApiKey) {
    headers['data-collection-secure-api-key'] = envConfig.secureApiKey;
  }
  
  config.headers = headers;
}

/**
 * Add authentication headers
 * @param {Object} config - Axios config
 */
function addAuthentication(config) {
  const token = TokenManager.getAccessToken();
  
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
    
    // Check if token is expired
    const isExpired = TokenManager.isTokenExpired(token);
    if (isExpired) {
      console.warn('⚠️ Using expired token, refresh may be needed');
    }
  }
}

/**
 * Generate unique request ID
 * @returns {string} Request ID
 */
function generateRequestId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}_${random}`;
}

/**
 * Log request details
 * @param {Object} config - Axios config
 */
function logRequest(config) {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const { requestId, method, url } = config.metadata;
  const hasBody = config.data !== undefined && config.data !== null;
  const token = TokenManager.getAccessToken();

  console.log(`API Request: ${method} ${url} [${requestId}]`);
  console.log(`Base URL: ${config.baseURL || 'N/A'}`);
  if (hasBody) {
    console.log('Request body: [redacted — may contain credentials]');
  }
  console.log('Headers: [redacted — may contain Authorization]');
  console.log(`Auth required: ${config.auth !== false}`);
  console.log(`Token present: ${Boolean(token)}; expired: ${token ? TokenManager.isTokenExpired(token) : 'n/a'}`);
}

/**
 * Request interceptor error handler
 * @param {Error} error - Request setup error
 * @returns {Promise<Error>} Rejected promise with error
 */
export const createRequestErrorHandler = () => {
  return function(error) {
    const safeMessage = error && typeof error.message === 'string' ? error.message : 'Unknown error';
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Setup Error:', safeMessage);
    } else {
      console.error('API request setup failed');
    }

    logError(error, 'API_REQUEST_SETUP');

    return Promise.reject(error);
  };
};

/**
 * Export token manager and functions for external use
 */
export { TokenManager, TokenRefreshManager };
